import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type Owner } from "../api";

/** Platform-admin-only screen. Lists every tenant on this portal and
 *  lets the admin create new ones. Admin enters the initial password
 *  directly; sharing it with the tenant is out-of-band (Zalo, SMS). */
export function OwnersAdminPage() {
  const [owners, setOwners] = useState<Owner[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function refresh() {
    try {
      setOwners(await api.get<Owner[]>("/owners"));
    } catch (e) {
      setError((e as Error).message);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function toggleDisabled(o: Owner) {
    const action = o.disabled_at ? "kích hoạt lại" : "vô hiệu hoá";
    if (!window.confirm(`${action[0].toUpperCase() + action.slice(1)} tenant ${o.email}?`)) return;
    try {
      await api.put<Owner>(`/owners/${o.id}`, { disabled: !o.disabled_at });
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Owners <small>Quản trị tenants</small>
        </h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setShowCreate(true)}>+ Tạo tenant</button>
      </div>

      {error && <div className="alert error">{error}</div>}

      {owners === null && <div className="muted">Đang tải…</div>}

      {owners && (
        <div className="card table-scroll" style={{ padding: 0, overflow: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Slug</th>
                <th>Tên hiển thị</th>
                <th>Plan</th>
                <th>Loại</th>
                <th>Trạng thái</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {owners.map((o) => (
                <tr key={o.id}>
                  <td><strong>{o.email}</strong></td>
                  <td><code className="inline">{o.slug}</code></td>
                  <td>{o.display_name || "—"}</td>
                  <td>{o.plan}</td>
                  <td>
                    {o.is_platform_admin ? (
                      <span className="badge admin">platform admin</span>
                    ) : (
                      <span className="badge owner">tenant</span>
                    )}
                  </td>
                  <td>
                    {o.disabled_at ? (
                      <span className="badge disabled">disabled</span>
                    ) : (
                      <span className="badge owner">active</span>
                    )}
                  </td>
                  <td>
                    {!o.is_platform_admin && (
                      <button
                        className={o.disabled_at ? "" : "danger"}
                        onClick={() => toggleDisabled(o)}
                        style={{ fontSize: "0.8rem", padding: "4px 10px" }}
                      >
                        {o.disabled_at ? "Kích hoạt" : "Disable"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateOwnerModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </>
  );
}

function CreateOwnerModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [farmName, setFarmName] = useState("Farm 1");
  const [plan, setPlan] = useState("free");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post<Owner>("/owners", {
        email, password,
        display_name: displayName,
        farm_name: farmName,
        plan,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Tạo tenant thất bại");
      setSubmitting(false);
    }
  }

  function genRandomPassword() {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let pw = "";
    const arr = new Uint32Array(16);
    crypto.getRandomValues(arr);
    for (const n of arr) pw += chars[n % chars.length];
    setPassword(pw);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Tạo tenant mới</h2>
        <p className="muted" style={{ fontSize: "0.85rem", marginTop: 0 }}>
          Tạo owner + user đầu tiên cho tenant. Gửi email + password cho chủ farm qua kênh
          out-of-band (Zalo, SMS, gọi điện). Họ đổi password sau lần đăng nhập đầu.
        </p>
        <form onSubmit={onSubmit}>
          {error && <div className="alert error">{error}</div>}

          <div className="field">
            <label>Email chủ farm</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label>Mật khẩu khởi tạo (min 8 ký tự)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                style={{ fontFamily: "monospace" }}
              />
              <button type="button" onClick={genRandomPassword}>Sinh random</button>
            </div>
          </div>

          <div className="form-row">
            <div className="field">
              <label>Tên hiển thị</label>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Nguyễn Văn A" />
            </div>
            <div className="field">
              <label>Plan</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label>Tên farm mặc định</label>
            <input value={farmName} onChange={(e) => setFarmName(e.target.value)} placeholder="Farm 1" />
          </div>

          <div className="btn-row">
            <button type="button" onClick={onClose}>Huỷ</button>
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? "Đang tạo…" : "Tạo tenant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
